/**
 * NotificationService - API client for system notification operations
 *
 * In development mode, delegates to mock data from NotificationService.mock.ts
 * In production, makes real API calls to the backend
 */

import { APIService, APIServiceError } from './APIService';
import type { NotificationConfig } from '../../types/notification';
import { mockGetNotification, mockUpdateNotification } from './NotificationService.mock';

const ADMIN_API_URL =
  import.meta.env['VITE_API_ADMIN_URL'] ||
  'https://k3xnba9ntf.execute-api.us-west-2.amazonaws.com/prod/admin';

const isDevelopment = import.meta.env.MODE === 'development';

export class NotificationService extends APIService {
  /**
   * Fetch current notification config (unauthenticated)
   */
  async getNotification(): Promise<NotificationConfig> {
    if (isDevelopment) {
      return mockGetNotification();
    }

    try {
      const response = await this.fetchWithRetry(`${ADMIN_API_URL}/notification`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        retries: 2,
        retryDelay: 1000,
        timeout: 10000,
      });

      return await response.json();
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }
      throw new APIServiceError('Failed to fetch notification', undefined, undefined, error);
    }
  }

  /**
   * Update notification config (admin auth required)
   */
  async updateNotification(enabled: boolean, message: string): Promise<NotificationConfig> {
    if (isDevelopment) {
      return mockUpdateNotification(enabled, message);
    }

    if (!this.isAuthenticated()) {
      throw new APIServiceError('User must be authenticated to update notifications', 401);
    }

    try {
      const response = await this.fetchWithRetry(`${ADMIN_API_URL}/notification`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify({ enabled, message }),
        retries: 2,
        retryDelay: 1000,
        timeout: 15000,
      });

      return await response.json();
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }
      throw new APIServiceError('Failed to update notification', undefined, undefined, error);
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
